import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import ScratchBlocks from 'scratch-blocks';
import {ICONS, iconSpan} from '../../lib/curo-icons.js';

/**
 * 학습 리포트 — 오늘 한 활동(학생정보 + AI 학습 요약 + 배운 내용)을 HTML/인쇄로 저장.
 * 세특·생기부 증빙용. python.cubroid.com "실행 리포트" 응용.
 * ⭐ 개인정보 안전: 전부 기기에서만 생성·저장(localStorage), 서버 전송 0.
 * MVP(2026-07-15): 학생정보·AI요약·배운내용. +07-22: 작품 썸네일(무대 캡처)·파이썬 코드·데이터 차트.
 * i18n: 11개 언어(en/ko/vi = MSG, ja/zh-cn/zh-tw/de/es/fr/it/ru = MSG_EXTRA, ja-Hira→ja 폴백).
 */

const INFO_KEY = 'curoai_report_info';
const NOTE_KEY = 'curoai_report_note';
const ML_EXT = 'ml2scratch2';

// 블록 카테고리 — opcode 접두사 → 카테고리 키 (핵심 8종 + 내 블록 + 확장 폴백)
const CAT_BY_PREFIX = {
    motion: 'catMotion', looks: 'catLooks', sound: 'catSound', event: 'catEvents',
    control: 'catControl', sensing: 'catSensing', operator: 'catOperators', operators: 'catOperators',
    data: 'catVariables', procedures: 'catMyBlocks', argument: 'catMyBlocks'
};
const CAT_ORDER = ['catMotion', 'catLooks', 'catSound', 'catEvents', 'catControl', 'catSensing',
    'catOperators', 'catVariables', 'catMyBlocks', 'catExtensions', 'catOther'];

const MSG = {
    title: {en: 'Learning Report', ko: '학습 리포트', vi: 'Báo cáo học tập'},
    sub: {en: 'Save what you did today — great for a portfolio.', ko: '오늘 한 걸 리포트로 저장하세요 — 세특·포트폴리오에 좋아요.', vi: 'Lưu những gì bạn đã làm hôm nay.'},
    student: {en: 'Student info', ko: '학생 정보', vi: 'Thông tin học sinh'},
    workSection: {en: 'My Project', ko: '내 작품', vi: 'Dự án của tôi'},
    pySection: {en: 'Python Code', ko: '파이썬 코드', vi: 'Mã Python'},
    pyLines: {en: 'lines', ko: '줄', vi: 'dòng'},
    codeSection: {en: 'Block Coding', ko: '블록 코딩', vi: 'Lập trình khối'},
    blkCount: {en: 'Blocks', ko: '블록 수', vi: 'Số khối'},
    blkKinds: {en: 'Block types', ko: '블록 종류', vi: 'Loại khối'},
    spriteCount: {en: 'Sprites', ko: '스프라이트', vi: 'Nhân vật'},
    catCol: {en: 'Category', ko: '카테고리', vi: 'Nhóm'},
    countCol: {en: 'Count', ko: '개수', vi: 'Số lượng'},
    catMotion: {en: 'Motion', ko: '동작', vi: 'Chuyển động'},
    catLooks: {en: 'Looks', ko: '형태', vi: 'Hiển thị'},
    catSound: {en: 'Sound', ko: '소리', vi: 'Âm thanh'},
    catEvents: {en: 'Events', ko: '이벤트', vi: 'Sự kiện'},
    catControl: {en: 'Control', ko: '제어', vi: 'Điều khiển'},
    catSensing: {en: 'Sensing', ko: '감지', vi: 'Cảm biến'},
    catOperators: {en: 'Operators', ko: '연산', vi: 'Phép tính'},
    catVariables: {en: 'Variables', ko: '변수', vi: 'Biến số'},
    catMyBlocks: {en: 'My Blocks', ko: '내 블록', vi: 'Khối của tôi'},
    catExtensions: {en: 'Extensions', ko: '확장', vi: 'Tiện ích'},
    catOther: {en: 'Other', ko: '기타', vi: 'Khác'},
    blkList: {en: 'Block List', ko: '블록 목록', vi: 'Danh sách khối'},
    stageName: {en: 'Stage', ko: '무대', vi: 'Sân khấu'},
    include: {en: 'Include', ko: '포함', vi: 'Bao gồm'},
    name: {en: 'Name', ko: '이름', vi: 'Tên'},
    school: {en: 'School', ko: '학교', vi: 'Trường'},
    grade: {en: 'Grade', ko: '학년', vi: 'Khối'},
    klass: {en: 'Class', ko: '반', vi: 'Lớp'},
    number: {en: 'No.', ko: '번호', vi: 'Số'},
    date: {en: 'Date', ko: '날짜', vi: 'Ngày'},
    aiSection: {en: 'AI Learning', ko: 'AI 학습', vi: 'Học AI'},
    aiNone: {en: '(AI Learning was not used)', ko: '(AI 학습을 사용하지 않았어요)', vi: '(Chưa dùng Học AI)'},
    aiClassCol: {en: 'Class', ko: '클래스', vi: 'Lớp'},
    aiCountCol: {en: 'Photos', ko: '사진', vi: 'Ảnh'},
    aiTotal: {en: 'Total', ko: '합계', vi: 'Tổng'},
    dataSection: {en: 'Data & Chart', ko: '데이터 & 차트', vi: 'Dữ liệu & Biểu đồ'},
    dataLabelCol: {en: 'Label', ko: '항목', vi: 'Nhãn'},
    dataValueCol: {en: 'Value', ko: '값', vi: 'Giá trị'},
    dataKind: {en: 'View', ko: '보기', vi: 'Kiểu'},
    kindBar: {en: 'Bar chart', ko: '막대 그래프', vi: 'Biểu đồ cột'},
    kindLine: {en: 'Line chart', ko: '꺾은선 그래프', vi: 'Biểu đồ đường'},
    kindPie: {en: 'Pie chart', ko: '원 그래프', vi: 'Biểu đồ tròn'},
    kindTable: {en: 'Table', ko: '표', vi: 'Bảng'},
    kindScatter: {en: 'Scatter plot', ko: '산점도', vi: 'Biểu đồ phân tán'},
    note: {en: 'What I learned', ko: '배운 내용', vi: 'Điều đã học'},
    notePh: {en: 'Write what you learned and how you felt…', ko: '오늘 배운 것, 느낀 점을 적어보세요…', vi: 'Viết điều bạn đã học và cảm nhận…'},
    privacy: {en: 'This report is created only on your device. Nothing is sent to a server.', ko: '이 리포트는 이 기기에서만 만들어져요. 서버로 전송되지 않습니다.', vi: 'Báo cáo này chỉ được tạo trên thiết bị của bạn. Không gửi lên máy chủ.'},
    saveHtml: {en: 'Save HTML', ko: 'HTML 저장', vi: 'Lưu HTML'},
    printBtn: {en: 'Print / PDF', ko: '인쇄 / PDF', vi: 'In / PDF'},
    close: {en: 'Close', ko: '닫기', vi: 'Đóng'},
    reportTitle: {en: 'Curo AI — Learning Report', ko: '큐로 AI — 학습 리포트', vi: 'Curo AI — Báo cáo học tập'},
    // ── 수업에 제출 (2026-08-17, 학생 계정 없이 수업 코드로) ──
    // vi 는 일부러 안 넣음 → en 폴백 (06-26 베트남어 오역 사고 후 방침: 추측 번역 금지, 원어민 검수 후 추가)
    classSection: {en: 'Submit to Class', ko: '수업에 제출'},
    classCode: {en: 'Class code (6 digits)', ko: '수업 코드 (6자리)'},
    classNick: {en: 'Nickname or number', ko: '별명 또는 번호 (실명 대신)'},
    classSubmit: {en: 'Submit', ko: '제출'},
    classSubmitting: {en: 'Submitting…', ko: '제출 중…'},
    classDone: {en: 'Submitted! My number:', ko: '제출 완료! 내 제출번호:'},
    classResubmit: {en: 'Submit again', ko: '다시 제출'},
    classFeedbackBtn: {en: 'Check feedback', ko: '피드백 보기'},
    classNotGraded: {en: 'Not graded yet — check again later.', ko: '아직 평가 전이에요 — 나중에 다시 확인해 보세요.'},
    classFeedback: {en: 'Teacher feedback', ko: '선생님 피드백'},
    classErrCode: {en: 'Class not found. Check the 6-digit code.', ko: '수업을 찾을 수 없어요. 6자리 코드를 확인하세요.'},
    classErrClosed: {en: 'This class is closed for submissions.', ko: '제출이 마감된 수업이에요.'},
    classErrNet: {en: 'Could not connect. Are you online?', ko: '연결하지 못했어요. 인터넷을 확인해 주세요.'},
    classPrivacy: {en: 'Submit sends this report to the Cubroid class server. Auto-deleted after 14 days.', ko: '제출을 누르면 이 리포트가 큐브로이드 수업 서버로 전송돼요. 14일 뒤 자동 삭제됩니다.'},
    rub1: {en: 'Works', ko: '작동해요'},
    rub2: {en: 'Structure', ko: '짜임새'},
    rub3: {en: 'Own idea', ko: '나만의 아이디어'},
    lv3: {en: 'Great', ko: '잘함'},
    lv2: {en: 'Good', ko: '보통'},
    lv1: {en: 'Keep trying', ko: '더 해보기'}
};
// 나머지 8개 언어 (웰컴/AI탭과 동일 세트: ja, zh-cn, zh-tw, de, es, fr, it, ru). en/ko/vi 는 위 MSG.
const MSG_EXTRA = {
    "ja": {"title": "学習レポート", "sub": "今日やったことをレポートに保存 — ポートフォリオに最適。", "student": "生徒情報", "workSection": "私の作品", "pySection": "Python コード", "pyLines": "行", "include": "含める", "name": "名前", "school": "学校", "grade": "学年", "klass": "クラス", "number": "番号", "date": "日付", "aiSection": "AI学習", "aiNone": "(AI学習は使いませんでした)", "aiClassCol": "クラス", "aiCountCol": "写真", "aiTotal": "合計", "dataSection": "データとグラフ", "dataLabelCol": "項目", "dataValueCol": "値", "dataKind": "表示", "kindBar": "棒グラフ", "kindLine": "折れ線グラフ", "kindPie": "円グラフ", "kindTable": "表", "kindScatter": "散布図", "note": "学んだこと", "notePh": "学んだこと・感じたことを書いてみよう…", "privacy": "このレポートはこの端末だけで作成されます。サーバーには送信されません。", "saveHtml": "HTML保存", "printBtn": "印刷 / PDF", "close": "閉じる", "codeSection": "ブロックコーディング", "blkCount": "ブロック数", "blkKinds": "ブロックの種類", "spriteCount": "スプライト", "catCol": "カテゴリー", "countCol": "個数", "catMotion": "動き", "catLooks": "見た目", "catSound": "音", "catEvents": "イベント", "catControl": "制御", "catSensing": "調べる", "catOperators": "演算", "catVariables": "変数", "catMyBlocks": "マイブロック", "catExtensions": "拡張機能", "catOther": "その他", "blkList": "ブロックリスト", "reportTitle": "Curo AI — 学習レポート"},
    "zh-cn": {"title": "学习报告", "sub": "保存你今天做的内容 — 很适合作品集。", "student": "学生信息", "workSection": "我的作品", "pySection": "Python 代码", "pyLines": "行", "include": "包含", "name": "姓名", "school": "学校", "grade": "年级", "klass": "班级", "number": "学号", "date": "日期", "aiSection": "AI学习", "aiNone": "(未使用 AI 学习)", "aiClassCol": "类别", "aiCountCol": "照片", "aiTotal": "合计", "dataSection": "数据与图表", "dataLabelCol": "项目", "dataValueCol": "值", "dataKind": "视图", "kindBar": "柱状图", "kindLine": "折线图", "kindPie": "饼图", "kindTable": "表格", "kindScatter": "散点图", "note": "学到的内容", "notePh": "写下你学到的和感受到的…", "privacy": "此报告仅在本设备上生成，不会发送到服务器。", "saveHtml": "保存 HTML", "printBtn": "打印 / PDF", "close": "关闭", "codeSection": "积木编程", "blkCount": "积木数", "blkKinds": "积木种类", "spriteCount": "角色", "catCol": "类别", "countCol": "数量", "catMotion": "运动", "catLooks": "外观", "catSound": "声音", "catEvents": "事件", "catControl": "控制", "catSensing": "侦测", "catOperators": "运算", "catVariables": "变量", "catMyBlocks": "自制积木", "catExtensions": "扩展", "catOther": "其他", "blkList": "积木列表", "reportTitle": "Curo AI — 学习报告"},
    "zh-tw": {"title": "學習報告", "sub": "儲存你今天完成的內容 — 很適合作品集。", "student": "學生資訊", "workSection": "我的作品", "pySection": "Python 程式碼", "pyLines": "行", "include": "包含", "name": "姓名", "school": "學校", "grade": "年級", "klass": "班級", "number": "座號", "date": "日期", "aiSection": "AI學習", "aiNone": "(未使用 AI 學習)", "aiClassCol": "類別", "aiCountCol": "照片", "aiTotal": "合計", "dataSection": "資料與圖表", "dataLabelCol": "項目", "dataValueCol": "值", "dataKind": "檢視", "kindBar": "長條圖", "kindLine": "折線圖", "kindPie": "圓餅圖", "kindTable": "表格", "kindScatter": "散佈圖", "note": "學到的內容", "notePh": "寫下你學到的和感受到的…", "privacy": "此報告僅在本裝置上產生，不會傳送到伺服器。", "saveHtml": "儲存 HTML", "printBtn": "列印 / PDF", "close": "關閉", "codeSection": "積木編程", "blkCount": "積木數", "blkKinds": "積木種類", "spriteCount": "角色", "catCol": "類別", "countCol": "數量", "catMotion": "動作", "catLooks": "外觀", "catSound": "音效", "catEvents": "事件", "catControl": "控制", "catSensing": "偵測", "catOperators": "運算", "catVariables": "變數", "catMyBlocks": "自訂積木", "catExtensions": "擴充", "catOther": "其他", "blkList": "積木列表", "reportTitle": "Curo AI — 學習報告"},
    "de": {"title": "Lernbericht", "sub": "Speichere, was du heute gemacht hast — ideal fürs Portfolio.", "student": "Schülerinfo", "workSection": "Mein Projekt", "pySection": "Python-Code", "pyLines": "Zeilen", "include": "Einschließen", "name": "Name", "school": "Schule", "grade": "Jahrgang", "klass": "Klasse", "number": "Nr.", "date": "Datum", "aiSection": "AI-Lernen", "aiNone": "(AI-Lernen wurde nicht verwendet)", "aiClassCol": "Klasse", "aiCountCol": "Fotos", "aiTotal": "Gesamt", "dataSection": "Daten & Diagramm", "dataLabelCol": "Bezeichnung", "dataValueCol": "Wert", "dataKind": "Ansicht", "kindBar": "Balkendiagramm", "kindLine": "Liniendiagramm", "kindPie": "Kreisdiagramm", "kindTable": "Tabelle", "kindScatter": "Streudiagramm", "note": "Was ich gelernt habe", "notePh": "Schreibe, was du gelernt und gefühlt hast…", "privacy": "Dieser Bericht wird nur auf deinem Gerät erstellt. Nichts wird an einen Server gesendet.", "saveHtml": "HTML speichern", "printBtn": "Drucken / PDF", "close": "Schließen", "codeSection": "Block-Programmierung", "blkCount": "Blöcke", "blkKinds": "Blockarten", "spriteCount": "Figuren", "catCol": "Kategorie", "countCol": "Anzahl", "catMotion": "Bewegung", "catLooks": "Aussehen", "catSound": "Klang", "catEvents": "Ereignisse", "catControl": "Steuerung", "catSensing": "Fühlen", "catOperators": "Operatoren", "catVariables": "Variablen", "catMyBlocks": "Meine Blöcke", "catExtensions": "Erweiterungen", "catOther": "Andere", "blkList": "Blockliste", "reportTitle": "Curo AI — Lernbericht"},
    "es": {"title": "Informe de aprendizaje", "sub": "Guarda lo que hiciste hoy — ideal para tu portafolio.", "student": "Datos del estudiante", "workSection": "Mi proyecto", "pySection": "Código Python", "pyLines": "líneas", "include": "Incluir", "name": "Nombre", "school": "Escuela", "grade": "Grado", "klass": "Clase", "number": "N.º", "date": "Fecha", "aiSection": "Aprendizaje AI", "aiNone": "(No se usó Aprendizaje AI)", "aiClassCol": "Clase", "aiCountCol": "Fotos", "aiTotal": "Total", "dataSection": "Datos y gráfico", "dataLabelCol": "Etiqueta", "dataValueCol": "Valor", "dataKind": "Vista", "kindBar": "Gráfico de barras", "kindLine": "Gráfico de líneas", "kindPie": "Gráfico circular", "kindTable": "Tabla", "kindScatter": "Diagrama de dispersión", "note": "Lo que aprendí", "notePh": "Escribe lo que aprendiste y cómo te sentiste…", "privacy": "Este informe se crea solo en tu dispositivo. No se envía nada a un servidor.", "saveHtml": "Guardar HTML", "printBtn": "Imprimir / PDF", "close": "Cerrar", "codeSection": "Programación por bloques", "blkCount": "Bloques", "blkKinds": "Tipos de bloque", "spriteCount": "Objetos", "catCol": "Categoría", "countCol": "Cantidad", "catMotion": "Movimiento", "catLooks": "Apariencia", "catSound": "Sonido", "catEvents": "Eventos", "catControl": "Control", "catSensing": "Sensores", "catOperators": "Operadores", "catVariables": "Variables", "catMyBlocks": "Mis bloques", "catExtensions": "Extensiones", "catOther": "Otros", "blkList": "Lista de bloques", "reportTitle": "Curo AI — Informe de aprendizaje"},
    "fr": {"title": "Rapport d'apprentissage", "sub": "Enregistre ce que tu as fait aujourd'hui — parfait pour ton portfolio.", "student": "Infos de l'élève", "workSection": "Mon projet", "pySection": "Code Python", "pyLines": "lignes", "include": "Inclure", "name": "Nom", "school": "École", "grade": "Niveau", "klass": "Classe", "number": "N°", "date": "Date", "aiSection": "Apprentissage AI", "aiNone": "(L'apprentissage AI n'a pas été utilisé)", "aiClassCol": "Classe", "aiCountCol": "Photos", "aiTotal": "Total", "dataSection": "Données et graphique", "dataLabelCol": "Étiquette", "dataValueCol": "Valeur", "dataKind": "Vue", "kindBar": "Diagramme à barres", "kindLine": "Graphique en courbes", "kindPie": "Diagramme circulaire", "kindTable": "Tableau", "kindScatter": "Nuage de points", "note": "Ce que j'ai appris", "notePh": "Écris ce que tu as appris et ressenti…", "privacy": "Ce rapport est créé uniquement sur ton appareil. Rien n'est envoyé à un serveur.", "saveHtml": "Enregistrer HTML", "printBtn": "Imprimer / PDF", "close": "Fermer", "codeSection": "Programmation par blocs", "blkCount": "Blocs", "blkKinds": "Types de blocs", "spriteCount": "Lutins", "catCol": "Catégorie", "countCol": "Nombre", "catMotion": "Mouvement", "catLooks": "Apparence", "catSound": "Son", "catEvents": "Événements", "catControl": "Contrôle", "catSensing": "Capteurs", "catOperators": "Opérateurs", "catVariables": "Variables", "catMyBlocks": "Mes blocs", "catExtensions": "Extensions", "catOther": "Autres", "blkList": "Liste des blocs", "reportTitle": "Curo AI — Rapport d'apprentissage"},
    "it": {"title": "Report di apprendimento", "sub": "Salva ciò che hai fatto oggi — ottimo per il portfolio.", "student": "Dati studente", "workSection": "Il mio progetto", "pySection": "Codice Python", "pyLines": "righe", "include": "Includi", "name": "Nome", "school": "Scuola", "grade": "Anno", "klass": "Classe", "number": "N.", "date": "Data", "aiSection": "Apprendimento AI", "aiNone": "(Apprendimento AI non usato)", "aiClassCol": "Classe", "aiCountCol": "Foto", "aiTotal": "Totale", "dataSection": "Dati e grafico", "dataLabelCol": "Etichetta", "dataValueCol": "Valore", "dataKind": "Vista", "kindBar": "Grafico a barre", "kindLine": "Grafico a linee", "kindPie": "Grafico a torta", "kindTable": "Tabella", "kindScatter": "Grafico a dispersione", "note": "Cosa ho imparato", "notePh": "Scrivi cosa hai imparato e come ti sei sentito…", "privacy": "Questo report viene creato solo sul tuo dispositivo. Niente viene inviato a un server.", "saveHtml": "Salva HTML", "printBtn": "Stampa / PDF", "close": "Chiudi", "codeSection": "Programmazione a blocchi", "blkCount": "Blocchi", "blkKinds": "Tipi di blocco", "spriteCount": "Sprite", "catCol": "Categoria", "countCol": "Quantità", "catMotion": "Movimento", "catLooks": "Aspetto", "catSound": "Suono", "catEvents": "Situazioni", "catControl": "Controllo", "catSensing": "Sensori", "catOperators": "Operatori", "catVariables": "Variabili", "catMyBlocks": "Blocchi miei", "catExtensions": "Estensioni", "catOther": "Altro", "blkList": "Elenco blocchi", "reportTitle": "Curo AI — Report di apprendimento"},
    "ru": {"title": "Отчёт об обучении", "sub": "Сохрани то, что сделал сегодня — отлично для портфолио.", "student": "Данные ученика", "workSection": "Мой проект", "pySection": "Код Python", "pyLines": "строк", "include": "Включить", "name": "Имя", "school": "Школа", "grade": "Класс", "klass": "Группа", "number": "№", "date": "Дата", "aiSection": "AI-обучение", "aiNone": "(AI-обучение не использовалось)", "aiClassCol": "Класс", "aiCountCol": "Фото", "aiTotal": "Итого", "dataSection": "Данные и график", "dataLabelCol": "Метка", "dataValueCol": "Значение", "dataKind": "Вид", "kindBar": "Столбчатая диаграмма", "kindLine": "Линейный график", "kindPie": "Круговая диаграмма", "kindTable": "Таблица", "kindScatter": "Точечная диаграмма", "note": "Что я узнал", "notePh": "Напиши, что ты узнал и что почувствовал…", "privacy": "Этот отчёт создаётся только на вашем устройстве. Ничего не отправляется на сервер.", "saveHtml": "Сохранить HTML", "printBtn": "Печать / PDF", "close": "Закрыть", "codeSection": "Блочное программирование", "blkCount": "Блоки", "blkKinds": "Виды блоков", "spriteCount": "Спрайты", "catCol": "Категория", "countCol": "Количество", "catMotion": "Движение", "catLooks": "Внешний вид", "catSound": "Звук", "catEvents": "События", "catControl": "Управление", "catSensing": "Сенсоры", "catOperators": "Операторы", "catVariables": "Переменные", "catMyBlocks": "Мои блоки", "catExtensions": "Расширения", "catOther": "Другое", "blkList": "Список блоков", "reportTitle": "Curo AI — Отчёт об обучении"}
};

class ReportModal extends React.Component {
    constructor (props) {
        super(props);
        let info = {name: '', school: '', grade: '', klass: '', number: '', on: true};
        let note = '';
        try { const s = localStorage.getItem(INFO_KEY); if (s) info = Object.assign(info, JSON.parse(s)); } catch (e) { /* noop */ }
        try { note = localStorage.getItem(NOTE_KEY) || ''; } catch (e) { /* noop */ }
        // 수업 제출 — 코드·별명은 기억(다음 시간에 또 쓰니까), 마지막 제출(id+viewKey)도 기억(피드백 조회용)
        let cls = {code: '', nick: '', last: null};
        try {
            cls.code = localStorage.getItem('curoai_class_code') || '';
            cls.nick = localStorage.getItem('curoai_class_nick') || '';
            const l = localStorage.getItem('curoai_class_last');
            if (l) cls.last = JSON.parse(l);
        } catch (e) { /* noop */ }
        this.state = {open: false, info, note, classes: [], chart: null, stageImg: null, pycode: '', blocks: null, blockList: [],
            clsCode: cls.code, clsNick: cls.nick, clsLast: cls.last, clsBusy: false, clsErr: '', clsFb: null};
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.submitToClass = this.submitToClass.bind(this);
        this.checkFeedback = this.checkFeedback.bind(this);
    }
    componentDidMount () { window.addEventListener('curoai-open-report', this.open); }
    componentWillUnmount () { window.removeEventListener('curoai-open-report', this.open); }
    t (key) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const ex = MSG_EXTRA[loc];
        if (ex && ex[key] != null) return ex[key];
        const m = MSG[key];
        if (!m) return key;
        return m[loc] || m.en || key;
    }
    _op (op, ext) {
        const rt = this.props.vm && this.props.vm.runtime;
        return (rt && rt.getOpcodeFunction) ? rt.getOpcodeFunction(`${ext || ML_EXT}_${op}`) : null;
    }
    open () {
        let classes = [];
        const gi = this._op('getClassInfo');
        try { classes = gi ? (gi() || []) : []; } catch (e) { classes = []; }
        let chart = null;
        const cd = this._op('getReportData', 'cubroidchart');
        try { const r = cd ? cd() : null; if (r && r.count > 0) chart = r; } catch (e) { chart = null; }
        // 파이썬 코드 — 에디터가 localStorage에 실시간 자동저장(curoai_python_code). 있을 때만 리포트에 실음.
        let pycode = '';
        try { pycode = localStorage.getItem('curoai_python_code') || ''; } catch (e) { pycode = ''; }
        pycode = pycode.replace(/\s+$/, '');
        if (!pycode.trim()) pycode = '';
        else if (pycode.length > 12000) pycode = `${pycode.slice(0, 12000)}\n…`; // 폭주 방지
        let blocks = null;
        try { blocks = this._blockStats(); } catch (e) { blocks = null; }
        let blockList = [];
        try { blockList = this._readBlocks(); } catch (e) { blockList = []; }
        this.setState({open: true, classes: classes.filter(c => c.count > 0), chart, stageImg: null, pycode, blocks, blockList});
        this._captureStage();
    }
    // 블록 코딩 요약 — VM에서 직접 집계(수정 없음). 그림자(기본 입력값)·클론 제외. 카테고리는 opcode 접두사로.
    _blockStats () {
        const rt = this.props.vm && this.props.vm.runtime;
        if (!rt || !rt.targets) return null;
        let total = 0;
        let sprites = 0;
        const opcodes = {};
        const cats = {};
        rt.targets.forEach(t => {
            if (!t || t.isOriginal === false) return; // 클론 제외
            if (!t.isStage) sprites++;
            const bl = t.blocks && t.blocks._blocks;
            if (!bl) return;
            Object.keys(bl).forEach(id => {
                const b = bl[id];
                if (!b || b.shadow) return; // 그림자(숫자·문자 기본 입력) 제외
                total++;
                opcodes[b.opcode] = 1;
                const pre = String(b.opcode || '').split('_')[0];
                const cat = CAT_BY_PREFIX[pre] || 'catExtensions';
                cats[cat] = (cats[cat] || 0) + 1;
            });
        });
        if (!total) return null;
        return {total, kinds: Object.keys(opcodes).length, sprites, cats};
    }
    // 실제 블록 문장 목록(아티보 "블록 목록" 대응). Blockly workspace의 blocks에서 문장을 뽑음 → 현재 언어로 자동 번역됨.
    // 현재 편집중 스프라이트=렌더된 메인 워크스페이스(안전), 나머지=헤드리스 워크스페이스에 XML 로드. 실패해도 조용히 스킵.
    _readBlocks () {
        if (!ScratchBlocks || !ScratchBlocks.Xml) return [];
        const rt = this.props.vm && this.props.vm.runtime;
        if (!rt || !rt.targets) return [];
        const targets = rt.targets.filter(t => t && t.isOriginal !== false);
        const editing = rt.getEditingTarget ? rt.getEditingTarget() : null;
        const groups = [];
        let ws = null;
        try { ws = new ScratchBlocks.Workspace(); } catch (e) { ws = null; }
        targets.forEach(t => {
            let lines = [];
            if (editing && t === editing) {
                try { lines = this._linesFromWs(ScratchBlocks.getMainWorkspace()); } catch (e) { lines = []; }
            }
            if ((!lines || !lines.length) && ws) lines = this._linesHeadless(t, ws);
            if (lines && lines.length) {
                groups.push({name: t.isStage ? this.t('stageName') : (t.getName ? t.getName() : ''), isStage: !!t.isStage, lines});
            }
        });
        if (ws) { try { ws.dispose(); } catch (e) { /* noop */ } }
        return groups;
    }
    // 대상 XML을 헤드리스 워크스페이스에 로드 후 문장 추출
    _linesHeadless (t, ws) {
        try {
            const xml = t.blocks && t.blocks.toXML ? t.blocks.toXML() : '';
            if (!xml) return [];
            const dom = ScratchBlocks.Xml.textToDom(`<xml xmlns="http://www.w3.org/1999/xhtml">${xml}</xml>`);
            ws.clear();
            ScratchBlocks.Xml.domToWorkspace(dom, ws);
            return this._linesFromWs(ws);
        } catch (e) { return []; }
    }
    // 워크스페이스의 최상위 스택들을 순서대로 걸어 문장 라인 배열 생성
    _linesFromWs (ws) {
        const out = [];
        if (!ws || !ws.getTopBlocks) return out;
        let tops = [];
        try { tops = ws.getTopBlocks(true) || []; } catch (e) { tops = []; }
        tops.forEach(top => {
            if (!top || top.outputConnection) return; // 홀로 놓인 리포터는 스킵
            let c = top;
            while (c) {
                if (!(c.isShadow && c.isShadow())) this._blockText(c, 0, out);
                c = c.getNextBlock ? c.getNextBlock() : null;
            }
            out.push({depth: -1, text: ''}); // 스택 사이 빈 줄
        });
        while (out.length && out[out.length - 1].text === '') out.pop();
        return out;
    }
    // 한 문장(명령/모자/C블록)의 얕은 텍스트 + 하위 스택(들여쓰기) 재귀. 다음 블록은 호출측이 처리.
    _blockText (b, depth, out) {
        try {
            const parts = [];
            (b.inputList || []).forEach(input => {
                (input.fieldRow || []).forEach(f => {
                    try { const tx = f.getText && f.getText(); if (tx != null && String(tx) !== '') parts.push(String(tx)); } catch (e) { /* noop */ }
                });
                if (input.connection && input.type === ScratchBlocks.INPUT_VALUE) {
                    const child = input.connection.targetBlock && input.connection.targetBlock();
                    if (child) parts.push(this._valueText(child));
                }
            });
            const line = parts.join(' ').replace(/\s+/g, ' ').trim();
            if (line) out.push({depth, text: line.length > 140 ? `${line.slice(0, 140)}…` : line});
        } catch (e) { /* noop */ }
        try {
            (b.inputList || []).forEach(input => {
                if (input.connection && input.type === ScratchBlocks.NEXT_STATEMENT) {
                    let c = input.connection.targetBlock && input.connection.targetBlock();
                    while (c) {
                        if (!(c.isShadow && c.isShadow())) this._blockText(c, depth + 1, out);
                        c = c.getNextBlock ? c.getNextBlock() : null;
                    }
                }
            });
        } catch (e) { /* noop */ }
    }
    // 리포터/입력값의 얕은 텍스트(값 입력만 재귀, 하위 스택 없음)
    _valueText (b) {
        try {
            const parts = [];
            (b.inputList || []).forEach(input => {
                (input.fieldRow || []).forEach(f => {
                    try { const tx = f.getText && f.getText(); if (tx != null && String(tx) !== '') parts.push(String(tx)); } catch (e) { /* noop */ }
                });
                if (input.connection && input.type === ScratchBlocks.INPUT_VALUE) {
                    const child = input.connection.targetBlock && input.connection.targetBlock();
                    if (child) parts.push(this._valueText(child));
                }
            });
            return parts.join(' ').trim();
        } catch (e) { return ''; }
    }
    // 무대(작품) 화면을 이미지로 캡처 — scratch-render의 requestSnapshot(다음 draw에 캡처). 강제 draw로 즉시.
    _captureStage () {
        const r = this.props.vm && this.props.vm.renderer;
        if (!r || !r.requestSnapshot) return;
        try {
            r.requestSnapshot(dataURL => {
                if (typeof dataURL === 'string' && dataURL.indexOf('data:image') === 0) this.setState({stageImg: dataURL});
            });
            if (r.draw) r.draw();
        } catch (e) { /* noop */ }
    }
    close () { this.setState({open: false}); }
    setInfo (k, v) {
        this.setState(prev => {
            const info = Object.assign({}, prev.info, {[k]: v});
            try { localStorage.setItem(INFO_KEY, JSON.stringify(info)); } catch (e) { /* noop */ }
            return {info};
        });
    }
    setNote (v) { this.setState({note: v}); try { localStorage.setItem(NOTE_KEY, v); } catch (e) { /* noop */ } }
    _kindLabel (chart) {
        if (!chart) return '';
        if (chart.mode === 'table') return this.t('kindTable');
        if (chart.mode === 'scatter') return this.t('kindScatter');
        return this.t({bar: 'kindBar', line: 'kindLine', pie: 'kindPie'}[chart.type] || 'kindBar');
    }
    _esc (s) { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c])); }
    _reportHtml () {
        const {info, note, classes} = this.state;
        const d = new Date();
        const pad = n => `0${n}`.slice(-2);
        const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        const total = classes.reduce((s, c) => s + (c.count || 0), 0);
        const infoRow = info.on ? `
            <table class="kv"><tbody>
              ${info.name ? `<tr><th>${this._esc(this.t('name'))}</th><td>${this._esc(info.name)}</td></tr>` : ''}
              ${info.school ? `<tr><th>${this._esc(this.t('school'))}</th><td>${this._esc(info.school)}</td></tr>` : ''}
              <tr><th>${this._esc(this.t('grade'))} / ${this._esc(this.t('klass'))} / ${this._esc(this.t('number'))}</th>
                  <td>${this._esc(info.grade)} / ${this._esc(info.klass)} / ${this._esc(info.number)}</td></tr>
            </tbody></table>` : '';
        const workSection = this.state.stageImg ?
            `<h2>${this._esc(this.t('workSection'))}</h2><img src="${this.state.stageImg}" style="width:320px;max-width:100%;border:1px solid #e0e3ec;border-radius:8px" alt="project"/>` : '';
        const b = this.state.blocks;
        const codeSection = b ? `
            <h2>${this._esc(this.t('codeSection'))}</h2>
            <p class="chips"><span class="chip"><b>${b.total}</b> ${this._esc(this.t('blkCount'))}</span> <span class="chip"><b>${b.kinds}</b> ${this._esc(this.t('blkKinds'))}</span> <span class="chip"><b>${b.sprites}</b> ${this._esc(this.t('spriteCount'))}</span></p>
            <table class="data"><thead><tr><th>${this._esc(this.t('catCol'))}</th><th>${this._esc(this.t('countCol'))}</th></tr></thead>
            <tbody>${CAT_ORDER.filter(c => b.cats[c]).map(c => `<tr><td>${this._esc(this.t(c))}</td><td>${b.cats[c]}</td></tr>`).join('')}</tbody></table>` : '';
        const bl = this.state.blockList;
        const blkListHtml = (bl && bl.length) ? `
            <h3 class="bhead">${this._esc(this.t('blkList'))}</h3>
            <div class="blk">${bl.map(g =>
                (bl.length > 1 ? `<div class="bsprite">${this._esc(g.name)}</div>` : '') +
                g.lines.map(ln => ln.text === '' ? '<div class="bgap"></div>' :
                    `<div class="bline" style="padding-left:${8 + (ln.depth > 0 ? ln.depth * 16 : 0)}px">${this._esc(ln.text)}</div>`).join('')
            ).join('')}</div>` : '';
        const pycode = this.state.pycode;
        const pySection = pycode ?
            `<h2>${this._esc(this.t('pySection'))} <span class="cnt">(${pycode.split('\n').length} ${this._esc(this.t('pyLines'))})</span></h2><pre class="code">${this._esc(pycode)}</pre>` : '';
        const chart = this.state.chart;
        const dataSection = chart ? `
            <h2>${this._esc(this.t('dataSection'))} · ${this._esc(this._kindLabel(chart))}</h2>
            <table class="data"><thead><tr><th>${this._esc(this.t('dataLabelCol'))}</th><th>${this._esc(this.t('dataValueCol'))}</th></tr></thead>
            <tbody>${chart.labels.slice(0, 30).map((lb, i) => `<tr><td>${this._esc(lb)}</td><td>${this._esc(chart.values[i])}</td></tr>`).join('')}</tbody></table>` : '';
        const aiSection = classes.length ? `
            <table class="data"><thead><tr><th>${this._esc(this.t('aiClassCol'))}</th><th>${this._esc(this.t('aiCountCol'))}</th></tr></thead>
            <tbody>
              ${classes.map(c => `<tr><td>${this._esc(c.name || c.label)}</td><td>${c.count}</td></tr>`).join('')}
              <tr class="total"><td>${this._esc(this.t('aiTotal'))}</td><td>${total}</td></tr>
            </tbody></table>` : `<p class="muted">${this._esc(this.t('aiNone'))}</p>`;
        return `<!doctype html><html><head><meta charset="utf-8"><title>${this._esc(this.t('reportTitle'))}</title>
        <style>
          body{font-family:"Helvetica Neue",Helvetica,Arial,"Malgun Gothic",sans-serif;color:#1f2540;max-width:720px;margin:24px auto;padding:0 20px;}
          h1{font-size:22px;border-bottom:3px solid #14c8a6;padding-bottom:8px;}
          h2{font-size:16px;color:#0a8f78;margin-top:26px;}
          .meta{color:#67708a;font-size:13px;margin-top:-4px;}
          table{border-collapse:collapse;width:100%;margin-top:8px;font-size:14px;}
          .kv th{text-align:left;width:36%;color:#67708a;font-weight:600;padding:6px 8px;vertical-align:top;}
          .kv td{padding:6px 8px;}
          .data th,.data td{border:1px solid #e0e3ec;padding:7px 10px;text-align:left;}
          .data th{background:#f4f6fb;}
          .data .total td{font-weight:bold;background:#f9fafc;}
          .muted{color:#9aa1b4;font-size:14px;}
          .chips{margin:8px 0 0;}
          .chip{display:inline-block;background:#eef7f4;color:#0a8f78;border-radius:14px;padding:4px 12px;margin:0 6px 6px 0;font-size:13px;}
          .chip b{font-size:15px;}
          .bhead{font-size:13px;color:#67708a;margin:14px 0 6px;}
          .blk{border:1px solid #e0e3ec;border-radius:8px;padding:8px 10px;background:#fafbfe;font-size:13px;line-height:1.65;}
          .bline{color:#2b3350;}
          .bsprite{font-weight:bold;color:#4a5169;margin:6px 0 2px;}
          .bgap{height:8px;}
          .cnt{color:#9aa1b4;font-size:12px;font-weight:normal;}
          .code{background:#f6f8fb;border:1px solid #e0e3ec;border-radius:8px;padding:12px;margin-top:8px;font-family:"SFMono-Regular",Consolas,"Liberation Mono",Menlo,monospace;font-size:12.5px;line-height:1.5;white-space:pre-wrap;word-break:break-word;overflow-x:auto;}
          .note{white-space:pre-wrap;border:1px solid #e0e3ec;border-radius:8px;padding:12px;min-height:60px;font-size:14px;line-height:1.6;}
          .foot{margin-top:28px;color:#8a90a3;font-size:12px;border-top:1px solid #eee;padding-top:10px;display:flex;align-items:center;gap:6px;}
          .foot svg,h1 svg{flex:none;}
          @media print{body{margin:0;}}
        </style></head><body>
        <h1><span style="color:#14c8a6;margin-right:8px;font-size:20px">${ICONS.doc}</span>${this._esc(this.t('reportTitle'))}</h1>
        <div class="meta">${this._esc(this.t('date'))}: ${dateStr}</div>
        ${info.on ? `<h2>${this._esc(this.t('student'))}</h2>${infoRow}` : ''}
        ${workSection}
        ${codeSection}
        ${blkListHtml}
        <h2>${this._esc(this.t('aiSection'))}</h2>${aiSection}
        ${dataSection}
        ${pySection}
        <h2>${this._esc(this.t('note'))}</h2><div class="note">${this._esc(note)}</div>
        <div class="foot"><span style="font-size:15px">${ICONS.lock}</span>${this._esc(this.t('privacy'))}</div>
        </body></html>`;
    }
    // ── 수업에 제출 (2026-08-17) ─────────────────────────────────
    // 제출물 = _reportHtml() 그대로 — 교사 페이지는 sandbox iframe 으로 렌더만 하므로 재구성 로직이 없다.
    // 같은 출처 상대경로(/api/class/) → nginx 가 127.0.0.1:8932 로 프록시. 로컬 npm start 에선 네트워크 에러로 떨어진다(안내 표시).
    submitToClass () {
        const code = String(this.state.clsCode || '').trim();
        if (!/^[0-9]{6}$/.test(code)) { this.setState({clsErr: this.t('classErrCode')}); return; }
        const nick = String(this.state.clsNick || '').trim().slice(0, 12);
        try {
            localStorage.setItem('curoai_class_code', code);
            localStorage.setItem('curoai_class_nick', nick);
        } catch (e) { /* noop */ }
        const aiTotal = this.state.classes.reduce((s2, c) => s2 + (c.count || 0), 0);
        const meta = {
            blocks: this.state.blocks ? this.state.blocks.total : 0,
            py: this.state.pycode ? this.state.pycode.split('\n').length : 0,
            ai: aiTotal
        };
        this.setState({clsBusy: true, clsErr: '', clsFb: null});
        fetch(`/api/class/sessions/${code}/submit`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({nick, meta, html: this._reportHtml()})
        }).then(r => r.json().then(j => ({status: r.status, j})))
            .then(({status, j}) => {
                if (status === 200 && j.id) {
                    const last = {code, id: j.id, vk: j.viewKey, ts: Date.now()};
                    try { localStorage.setItem('curoai_class_last', JSON.stringify(last)); } catch (e) { /* noop */ }
                    this.setState({clsBusy: false, clsLast: last});
                    try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({event: 'curo_class_submit'}); } catch (e) { /* noop */ }
                    return;
                }
                const msg = j.error === 'closed' ? this.t('classErrClosed') :
                    (j.error === 'nosession' ? this.t('classErrCode') : this.t('classErrNet'));
                this.setState({clsBusy: false, clsErr: msg});
            })
            .catch(() => this.setState({clsBusy: false, clsErr: this.t('classErrNet')}));
    }
    checkFeedback () {
        const l = this.state.clsLast;
        if (!l) return;
        this.setState({clsBusy: true, clsErr: ''});
        fetch(`/api/class/sessions/${l.code}/feedback/${l.id}?vk=${l.vk}`)
            .then(r => r.json())
            .then(j => {
                this.setState({clsBusy: false, clsFb: j});
                try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({event: 'curo_class_feedback_view'}); } catch (e) { /* noop */ }
            })
            .catch(() => this.setState({clsBusy: false, clsErr: this.t('classErrNet')}));
    }
    saveHtml () {
        const blob = new Blob([this._reportHtml()], {type: 'text/html'});
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const d = new Date();
        const pad = n => `0${n}`.slice(-2);
        a.download = `curoai-report-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}.html`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    }
    print () {
        const w = window.open('', '_blank');
        if (!w) return;
        w.document.write(this._reportHtml());
        w.document.close();
        w.focus();
        setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 350);
    }
    // 수업 제출 박스. 마지막 제출이 "지금 입력된 코드"와 같으면 완료 화면(+피드백), 아니면 입력 폼.
    _renderClass () {
        const {clsCode, clsNick, clsLast, clsBusy, clsErr, clsFb} = this.state;
        const doneHere = clsLast && clsLast.code === String(clsCode).trim();
        const fb = clsFb;
        return (
            <div style={S.clsBox} data-testid="curoClassSubmit">
                <div style={S.clsTitle}>
                    <span {...iconSpan('save', {marginRight: 6})} />{this.t('classSection')}
                </div>
                {doneHere ? (
                    <div>
                        <div style={S.clsDone}>
                            {`${this.t('classDone')} `}<b style={S.clsDoneId}>{clsLast.id}</b>
                        </div>
                        <div style={S.clsRow}>
                            <button style={S.clsBtn} disabled={clsBusy} onClick={this.checkFeedback}>{this.t('classFeedbackBtn')}</button>
                            <button
                                style={S.clsBtnGhost}
                                disabled={clsBusy}
                                onClick={() => this.setState({clsLast: null, clsFb: null, clsErr: ''})}
                            >{this.t('classResubmit')}</button>
                        </div>
                        {fb ? (fb.graded ? (
                            <div style={S.fbBox}>
                                <div style={S.fbTitle}>{this.t('classFeedback')}</div>
                                {[0, 1, 2].map(i => (
                                    <div key={i} style={S.fbRow}>
                                        <span>{this.t(`rub${i + 1}`)}</span>
                                        <b style={S.fbLv}>{this.t(`lv${fb.rubric[i]}`)}</b>
                                    </div>
                                ))}
                                {fb.comment ? <div style={S.fbComment}>{fb.comment}</div> : null}
                            </div>
                        ) : <div style={S.clsMuted}>{this.t('classNotGraded')}</div>) : null}
                    </div>
                ) : (
                    <div>
                        <div style={S.clsRow}>
                            <input
                                style={Object.assign({}, S.input, {width: 110, flex: 'none'})}
                                placeholder={this.t('classCode')}
                                value={clsCode}
                                maxLength={6}
                                inputMode="numeric"
                                onChange={e => this.setState({clsCode: e.target.value.replace(/[^0-9]/g, ''), clsErr: ''})}
                            />
                            <input
                                style={Object.assign({}, S.input, {flex: 1})}
                                placeholder={this.t('classNick')}
                                value={clsNick}
                                maxLength={12}
                                onChange={e => this.setState({clsNick: e.target.value})}
                            />
                            <button style={S.clsBtn} disabled={clsBusy} onClick={this.submitToClass}>
                                {clsBusy ? this.t('classSubmitting') : this.t('classSubmit')}
                            </button>
                        </div>
                        {clsErr ? <div style={S.clsErr}>{clsErr}</div> : null}
                        <div style={S.clsMuted}>{this.t('classPrivacy')}</div>
                    </div>
                )}
            </div>
        );
    }
    render () {
        if (!this.state.open) return null;
        const {info, note, classes} = this.state;
        const fields = [['name', 8], ['school', 8], ['grade', 3], ['klass', 3], ['number', 3]];
        const total = classes.reduce((s, c) => s + (c.count || 0), 0);
        return (
            <div style={S.overlay} onClick={this.close}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                    <div style={S.head}>
                        <span style={S.title}>
                            <span {...iconSpan('doc', {marginRight: 7, color: '#14c8a6'})} />
                            {this.t('title')}
                        </span>
                        <span style={S.actions}>
                            <button style={S.hSave} onClick={() => this.saveHtml()}>
                                <span {...iconSpan('save', {marginRight: 4})} />{this.t('saveHtml')}
                            </button>
                            <button style={S.hPrint} onClick={() => this.print()}>
                                <span {...iconSpan('print', {marginRight: 4})} />{this.t('printBtn')}
                            </button>
                            <button style={S.hClose} onClick={this.close}>{this.t('close')}</button>
                        </span>
                    </div>
                    <div style={S.sub}>{this.t('sub')}</div>
                    <div style={S.body}>
                        <div style={S.secRow}>
                            <span style={S.secTitle}>{this.t('student')}</span>
                            <label style={S.toggle}>
                                <input
                                    type="checkbox"
                                    checked={info.on}
                                    onChange={e => this.setInfo('on', e.target.checked)}
                                />{' '}{this.t('include')}
                            </label>
                        </div>
                        {info.on ? (
                            <div style={S.form}>
                                {fields.map(([k, flex]) => (
                                    <input
                                        key={k}
                                        style={Object.assign({}, S.input, {flex})}
                                        placeholder={this.t(k)}
                                        value={info[k]}
                                        maxLength={k === 'name' || k === 'school' ? 24 : 6}
                                        onChange={e => this.setInfo(k, e.target.value)}
                                    />
                                ))}
                            </div>
                        ) : null}

                        {this.state.stageImg ? (
                            <div>
                                <div style={S.secTitle}>{this.t('workSection')}</div>
                                <img src={this.state.stageImg} style={S.stageImg} alt="project" />
                            </div>
                        ) : null}

                        {this.state.blocks ? (
                            <div>
                                <div style={S.secTitle}>{this.t('codeSection')}</div>
                                <div style={S.chips}>
                                    <span style={S.chip}><b style={S.chipB}>{this.state.blocks.total}</b>{` ${this.t('blkCount')}`}</span>
                                    <span style={S.chip}><b style={S.chipB}>{this.state.blocks.kinds}</b>{` ${this.t('blkKinds')}`}</span>
                                    <span style={S.chip}><b style={S.chipB}>{this.state.blocks.sprites}</b>{` ${this.t('spriteCount')}`}</span>
                                </div>
                                <table style={S.table}>
                                    <thead><tr><th style={S.th}>{this.t('catCol')}</th><th style={S.thN}>{this.t('countCol')}</th></tr></thead>
                                    <tbody>
                                        {CAT_ORDER.filter(c => this.state.blocks.cats[c]).map(c => (
                                            <tr key={c}><td style={S.td}>{this.t(c)}</td><td style={S.tdN}>{this.state.blocks.cats[c]}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                                {this.state.blockList && this.state.blockList.length ? (
                                    <div>
                                        <div style={S.blkHead}>{this.t('blkList')}</div>
                                        <div style={S.blkBox}>
                                            {this.state.blockList.map((g, gi) => (
                                                <div key={gi}>
                                                    {this.state.blockList.length > 1 ? <div style={S.blkSprite}>{g.name}</div> : null}
                                                    {g.lines.map((ln, li) => (
                                                        ln.text === '' ?
                                                            <div key={li} style={S.blkGap} /> :
                                                            <div key={li} style={Object.assign({}, S.blkLine, {paddingLeft: 8 + (ln.depth > 0 ? ln.depth * 16 : 0)})}>{ln.text}</div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        <div style={S.secTitle}>{this.t('aiSection')}</div>
                        {classes.length ? (
                            <table style={S.table}>
                                <thead><tr><th style={S.th}>{this.t('aiClassCol')}</th><th style={S.thN}>{this.t('aiCountCol')}</th></tr></thead>
                                <tbody>
                                    {classes.map(c => (
                                        <tr key={c.label}><td style={S.td}>{c.name || c.label}</td><td style={S.tdN}>{c.count}</td></tr>
                                    ))}
                                    <tr><td style={S.tdTotal}>{this.t('aiTotal')}</td><td style={S.tdTotalN}>{total}</td></tr>
                                </tbody>
                            </table>
                        ) : <div style={S.muted}>{this.t('aiNone')}</div>}

                        {this.state.chart ? (
                            <div>
                                <div style={S.secTitle}>{`${this.t('dataSection')} · ${this._kindLabel(this.state.chart)}`}</div>
                                <table style={S.table}>
                                    <thead><tr><th style={S.th}>{this.t('dataLabelCol')}</th><th style={S.thN}>{this.t('dataValueCol')}</th></tr></thead>
                                    <tbody>
                                        {this.state.chart.labels.slice(0, 30).map((lb, i) => (
                                            <tr key={i}><td style={S.td}>{String(lb)}</td><td style={S.tdN}>{String(this.state.chart.values[i])}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}

                        {this.state.pycode ? (
                            <div>
                                <div style={S.secTitle}>
                                    {this.t('pySection')}
                                    <span style={S.cnt}>{` (${this.state.pycode.split('\n').length} ${this.t('pyLines')})`}</span>
                                </div>
                                <pre style={S.code}>{this.state.pycode}</pre>
                            </div>
                        ) : null}

                        <div style={S.secTitle}>{this.t('note')}</div>
                        <textarea
                            style={S.textarea}
                            placeholder={this.t('notePh')}
                            value={note}
                            onChange={e => this.setNote(e.target.value)}
                        />
                        {this._renderClass()}

                        <div style={S.privacy}>
                            <span {...iconSpan('lock', {marginRight: 5, verticalAlign: '-0.15em'})} />{this.t('privacy')}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const S = {
    overlay: {position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999},
    modal: {background: '#fff', borderRadius: 14, width: 'min(560px, 94vw)', maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 12px 40px rgba(0,0,0,0.3)', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    head: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '16px 20px 0'},
    title: {fontSize: 18, fontWeight: 'bold', color: '#1f2540', display: 'inline-flex', alignItems: 'center'},
    actions: {display: 'flex', gap: 6},
    hSave: {display: 'inline-flex', alignItems: 'center', padding: '6px 12px', border: 'none', borderRadius: 16, background: '#14c8a6', color: '#fff', fontSize: 12, cursor: 'pointer'},
    hPrint: {display: 'inline-flex', alignItems: 'center', padding: '6px 12px', border: 'none', borderRadius: 16, background: '#4c97ff', color: '#fff', fontSize: 12, cursor: 'pointer'},
    hClose: {padding: '6px 12px', border: 'none', borderRadius: 16, background: '#eef0f5', color: '#4a5169', fontSize: 12, cursor: 'pointer'},
    sub: {fontSize: 12, color: '#67708a', padding: '4px 20px 0'},
    body: {padding: '10px 20px 20px'},
    secRow: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14},
    secTitle: {fontSize: 14, fontWeight: 'bold', color: '#0a8f78', marginTop: 16, marginBottom: 6},
    toggle: {fontSize: 12, color: '#67708a', cursor: 'pointer'},
    form: {display: 'flex', gap: 6, flexWrap: 'wrap'},
    input: {padding: '7px 9px', border: '1px solid #d9dce4', borderRadius: 8, fontSize: 13, minWidth: 0},
    table: {borderCollapse: 'collapse', width: '100%', fontSize: 13},
    th: {border: '1px solid #e0e3ec', padding: '6px 9px', textAlign: 'left', background: '#f4f6fb'},
    thN: {border: '1px solid #e0e3ec', padding: '6px 9px', textAlign: 'right', background: '#f4f6fb', width: 80},
    td: {border: '1px solid #e0e3ec', padding: '6px 9px'},
    tdN: {border: '1px solid #e0e3ec', padding: '6px 9px', textAlign: 'right'},
    tdTotal: {border: '1px solid #e0e3ec', padding: '6px 9px', fontWeight: 'bold', background: '#f9fafc'},
    tdTotalN: {border: '1px solid #e0e3ec', padding: '6px 9px', textAlign: 'right', fontWeight: 'bold', background: '#f9fafc'},
    muted: {color: '#9aa1b4', fontSize: 13},
    chips: {display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4},
    chip: {display: 'inline-block', background: '#eef7f4', color: '#0a8f78', borderRadius: 14, padding: '4px 12px', fontSize: 13},
    chipB: {fontSize: 15, marginRight: 3},
    blkHead: {fontSize: 12, fontWeight: 'bold', color: '#67708a', margin: '12px 0 5px'},
    blkBox: {border: '1px solid #e0e3ec', borderRadius: 8, padding: '8px 10px', maxHeight: 220, overflow: 'auto', background: '#fafbfe', fontSize: 13, lineHeight: 1.65},
    blkSprite: {fontWeight: 'bold', color: '#4a5169', margin: '6px 0 2px'},
    blkLine: {color: '#2b3350', whiteSpace: 'pre-wrap'},
    blkGap: {height: 8},
    cnt: {color: '#9aa1b4', fontSize: 11, fontWeight: 'normal', marginLeft: 6},
    code: {background: '#f6f8fb', border: '1px solid #e0e3ec', borderRadius: 8, padding: 12, margin: 0,
        fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace', fontSize: 12,
        lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto'},
    stageImg: {width: 240, maxWidth: '100%', border: '1px solid #e0e3ec', borderRadius: 8, display: 'block'},
    textarea: {width: '100%', minHeight: 90, padding: 10, border: '1px solid #d9dce4', borderRadius: 8,
        fontSize: 13, lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'},
    privacy: {marginTop: 14, fontSize: 12, color: '#8a90a3', background: '#f6f8fb', borderRadius: 8, padding: '8px 10px'},
    // ── 수업에 제출 ──
    clsBox: {marginTop: 18, border: '2px solid #14c8a6', borderRadius: 10, padding: '12px 14px', background: '#f4fbf9'},
    clsTitle: {fontSize: 14, fontWeight: 'bold', color: '#0a8f78', marginBottom: 8, display: 'inline-flex', alignItems: 'center'},
    clsRow: {display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center'},
    clsBtn: {padding: '8px 16px', border: 'none', borderRadius: 16, background: '#14c8a6', color: '#fff', fontSize: 13, fontWeight: 'bold', cursor: 'pointer'},
    clsBtnGhost: {padding: '8px 14px', border: '1px solid #b9c0cf', borderRadius: 16, background: '#fff', color: '#4a5169', fontSize: 12, cursor: 'pointer'},
    clsErr: {marginTop: 8, fontSize: 12, color: '#d43f3f'},
    clsMuted: {marginTop: 8, fontSize: 11.5, color: '#8a90a3'},
    clsDone: {fontSize: 13, color: '#0a8f78', marginBottom: 8},
    clsDoneId: {fontSize: 16, marginLeft: 2},
    fbBox: {marginTop: 10, border: '1px solid #cfe8e1', borderRadius: 8, padding: '10px 12px', background: '#fff'},
    fbTitle: {fontSize: 12.5, fontWeight: 'bold', color: '#0a8f78', marginBottom: 6},
    fbRow: {display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: '#2b3350'},
    fbLv: {color: '#0a8f78'},
    fbComment: {marginTop: 8, fontSize: 13, lineHeight: 1.55, color: '#2b3350', whiteSpace: 'pre-wrap', borderTop: '1px dashed #dfe5ee', paddingTop: 8}
};

ReportModal.propTypes = {
    intl: PropTypes.object,
    vm: PropTypes.object
};

export default injectIntl(ReportModal);
